import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { projectService, caseService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ProjectWizard from '../components/ProjectWizard';
import CreateCaseModal from '../components/CreateCaseModal';
import ManageUsersModal from '../components/ManageUsersModal';

const ProjectDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cases');
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);

  useEffect(() => {
    fetchProject();
    if (activeTab === 'cases') {
      fetchCases();
    }
  }, [id, activeTab]);


  const fetchProject = async () => {
    try {
      const data = await projectService.getProject(id);
      setProject(data);
    } catch (error) {
      toast.error(t('workspaces.fetchError'));
      console.error('Error fetching workspace:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCases = async () => {
    setCasesLoading(true);
    try {
      const casesData = await caseService.getCases(id, true);
      // Cases should already have case_type from the API
      const enhancedCases = casesData.map((case_) => ({
        ...case_,
        workspace_id: id
      }));
      setCases(enhancedCases);
    } catch (error) {
      console.error('Error fetching cases:', error);
      // Fallback to simulated data if API fails
      const simulatedCases = [
        {
          id: 'case-1',
          name: 'Economic Crisis Scenario 2025',
          description: 'Strategic analysis of potential economic downturns and market volatility',
          case_type: 'scenario_analysis',
          category_system: 'PESTEL',
          time_horizon: '2025-2027',
          is_archived: false,
          workspace_id: id,
          created_at: new Date(Date.now() - 86400000).toISOString()
        },
        {
          id: 'case-2', 
          name: 'Technology Disruption Assessment',
          description: 'Evaluating risks from emerging technologies in our sector',
          case_type: 'risk_assessment',
          category_system: 'PEST',
          time_horizon: '2024-2030',
          is_archived: false,
          workspace_id: id,
          created_at: new Date(Date.now() - 172800000).toISOString()
        }
      ];
      setCases(simulatedCases);
    } finally {
      setCasesLoading(false);
    }
  };

  const handleCreateCase = async (caseData) => {
    try {
      await caseService.createCase(caseData);
      setShowCreateCaseModal(false);
      fetchCases(); // Refresh cases list
    } catch (error) {
      toast.error(t('cases.createError'));
      console.error('Error creating case:', error);
    }
  };

  const handleArchiveCase = async (caseId, isArchived) => {
    const action = isArchived ? 'unarchive' : 'archive';
    const confirmMessage = isArchived 
      ? t('cases.confirmUnarchive')
      : t('cases.confirmArchive');
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await caseService.updateCase(caseId, { is_archived: !isArchived });
      fetchCases(); // Refresh cases list
    } catch (error) {
      toast.error(t(`cases.${action}Error`));
      console.error(`Error ${action}ing case:`, error);
    }
  };

  const handleDeleteCase = async (caseId, caseName) => {
    if (!window.confirm(t('cases.confirmDelete', { name: caseName }))) {
      return;
    }
    
    if (!window.confirm(t('cases.confirmDeleteFinal'))) {
      return;
    }

    try {
      await caseService.deleteCase(caseId);
      fetchCases(); // Refresh cases list
    } catch (error) {
      toast.error(t('cases.deleteError'));
      console.error('Error deleting case:', error);
    }
  };

  const handleArchiveWorkspace = async () => {
    if (!window.confirm(t('workspaces.confirmArchive'))) {
      return;
    }
    
    try {
      await projectService.updateProject(project.id, { is_archived: true });
      setProject(prev => ({ ...prev, is_archived: true }));
    } catch (error) {
      toast.error(t('workspaces.archiveError'));
      console.error('Error archiving workspace:', error);
    }
  };

  const handleUnarchiveWorkspace = async () => {
    try {
      await projectService.updateProject(project.id, { is_archived: false });
      setProject(prev => ({ ...prev, is_archived: false }));
    } catch (error) {
      toast.error(t('workspaces.unarchiveError'));
      console.error('Error unarchiving workspace:', error);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!window.confirm(t('workspaces.confirmDelete', { name: project.name }))) {
      return;
    }
    
    if (!window.confirm(t('workspaces.confirmDeleteFinal'))) {
      return;
    }

    try {
      // TODO: Implement delete API call
      await projectService.deleteProject(project.id);
      // Redirect to workspaces list
      window.location.href = '/workspaces';
    } catch (error) {
      toast.error(t('workspaces.deleteError'));
      console.error('Error deleting workspace:', error);
    }
  };

  // Check if current user can manage users for this workspace
  const canManageUsers = () => {
    // Always allow if user is admin (regardless of case)
    if (currentUser?.role?.toLowerCase() === 'administrator') return true;
    
    // Allow instructors for workspaces they created
    if (currentUser?.role?.toLowerCase() === 'instructor' && project?.creator_id === currentUser?.id) return true;
    
    return false;
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center">{t('common.loading')}</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center text-error-600">{t('workspaces.notFound')}</div>
      </div>
    );
  }

  const tabs = [
    { id: 'cases', name: t('workspaces.cases'), current: activeTab === 'cases' },
    { id: 'users', name: t('workspaces.analysts'), current: activeTab === 'users' }
  ];

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="border-b border-neutral-200 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-4">
                <li>
                  <Link to="/workspaces" className="text-neutral-500 hover:text-neutral-700 text-sm">
                    {t('workspaces.title')}
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-neutral-300"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                    </svg>
                    <span className="ml-4 text-sm font-medium text-neutral-600">
                      {project.name}
                    </span>
                  </div>
                </li>
              </ol>
            </nav>
            <h1 className="mt-2 text-2xl font-semibold text-neutral-900">{project.name}</h1>
            {project.description && (
              <p className="mt-1 text-sm text-neutral-600">{project.description}</p>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            {project.is_archived && (
              <span className="inline-flex rounded-md bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-700">
                {t('common.archived')}
              </span>
            )}
            <div className="text-sm text-neutral-500">
              <div>{t('workspaces.timeHorizon')}: {project.time_horizon || 'Not specified'}</div>
              <div>{t('workspaces.framework')}: {project.category_system}</div>
            </div>
            
            {/* Workspace Actions */}
            <div className="flex items-center space-x-2">
              {canManageUsers() && (
                <button
                  onClick={() => setShowManageUsersModal(true)}
                  className="px-3 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 border border-accent-300 rounded-md hover:bg-accent-50 transition-colors"
                >
                  {t('workspaces.manageUsers')}
                </button>
              )}
              {canManageUsers() && (
                <>
                  {!project.is_archived ? (
                    <button
                      onClick={() => handleArchiveWorkspace()}
                      className="px-3 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
                    >
                      {t('workspaces.archive')}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUnarchiveWorkspace()}
                      className="px-3 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 border border-accent-300 rounded-md hover:bg-accent-50 transition-colors"
                    >
                      {t('workspaces.unarchive')}
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteWorkspace()}
                    className="px-3 py-2 text-sm font-medium text-error-600 hover:text-error-700 border border-error-300 rounded-md hover:bg-error-50 transition-colors"
                  >
                    {t('common.delete')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6">
        <div className="border-b border-neutral-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  tab.current
                    ? 'border-accent-500 text-accent-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'cases' && (
          <div className="space-y-6">
            {/* Cases Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-neutral-900">{t('workspaces.casesInWorkspace')}</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  {t('workspaces.casesDescription', { name: project.name })}
                </p>
              </div>
              <button
                onClick={() => setShowCreateCaseModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 transition-colors"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('cases.create')}
              </button>
            </div>

            {/* Cases List */}
            <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
              {casesLoading ? (
                <div className="p-6 text-center text-neutral-500">
                  Loading cases...
                </div>
              ) : cases.length === 0 ? (
                <div className="p-6 text-center text-neutral-500">
                  <div className="w-12 h-12 bg-neutral-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-sm font-medium text-neutral-900 mb-2">No cases yet</h4>
                  <p className="text-sm text-neutral-500 mb-4">
                    Create your first case to start developing strategic scenarios within this workspace.
                  </p>
                  <button
                    onClick={() => setShowCreateCaseModal(true)}
                    className="text-accent-600 hover:text-accent-700 font-medium text-sm"
                  >
                    Create your first case
                  </button>
                </div>
              ) : (
                <ul className="divide-y divide-neutral-200">
                  {cases.map((case_) => (
                    <li key={case_.id} className="hover:bg-neutral-50 transition-colors">
                      <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-start space-x-4 flex-1">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <h4 className="text-sm font-medium text-neutral-900 truncate">
                                  {case_.name}
                                </h4>
                                {case_.is_archived && (
                                  <span className="inline-flex rounded-md bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-700">
                                    {t('common.archived')}
                                  </span>
                                )}
                                <span className="inline-flex rounded-md bg-accent-100 px-2 py-1 text-xs font-medium text-accent-700 capitalize">
                                  {case_.case_type && t(`cases.types.${case_.case_type}`, case_.case_type?.replace('_', ' '))}
                                </span>
                              </div>
                              <p className="text-sm text-neutral-500 mt-1 truncate">
                                {case_.description}
                              </p>
                              <div className="flex items-center mt-2 text-xs text-neutral-400 space-x-4">
                                <span>{t('workspaces.framework')}: {case_.category_system}</span>
                                <span>{t('common.created')}: {new Date(case_.created_at).toLocaleDateString()}</span>
                                {case_.time_horizon && (
                                  <span>{t('workspaces.timeHorizon')}: {case_.time_horizon}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Case Actions */}
                          <div className="flex items-center space-x-2 ml-4">
                            {canManageUsers() && (
                              <>
                                <button
                                  onClick={() => handleArchiveCase(case_.id, case_.is_archived)}
                                  className={`px-2 py-1 text-xs font-medium border rounded transition-colors ${
                                    case_.is_archived 
                                      ? 'text-accent-600 border-accent-300 hover:bg-accent-50' 
                                      : 'text-neutral-600 border-neutral-300 hover:bg-neutral-50'
                                  }`}
                                >
                                  {case_.is_archived ? t('workspaces.unarchive') : t('workspaces.archive')}
                                </button>
                                
                                <button
                                  onClick={() => handleDeleteCase(case_.id, case_.name)}
                                  className="px-2 py-1 text-xs font-medium text-error-600 border border-error-300 rounded hover:bg-error-50 transition-colors"
                                >
                                  {t('common.delete')}
                                </button>
                              </>
                            )}
                            
                            <Link
                              to={`/workspaces/${id}/cases/${case_.id}`}
                              className="px-3 py-1 text-xs font-medium text-accent-600 bg-accent-50 rounded hover:bg-accent-100 transition-colors"
                            >
                              {t('cases.openCase')}
                            </Link>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Analysts Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-neutral-900">{t('workspaces.assignedAnalysts')}</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  {t('workspaces.analystsDescription')}
                </p>
              </div>
              {canManageUsers() && (
                <button
                  onClick={() => setShowManageUsersModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 transition-colors"
                >
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('workspaces.addRemoveAnalysts')}
                </button>
              )}
            </div>

            {/* Users List */}
            <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
              {project.participants && project.participants.length > 0 ? (
                <ul className="divide-y divide-neutral-200">
                  {project.participants.map((user) => (
                    <li key={user.id} className="px-6 py-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
                          <span className="text-accent-700 font-medium text-sm">
                            {user.full_name ? user.full_name.split(' ').map(n => n[0]).slice(0, 2).join('') : user.username[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-neutral-900">
                              {user.full_name || user.username}
                            </h4>
                            <span className="text-xs text-neutral-500">
                              @{user.username}
                            </span>
                            <span className="inline-flex rounded-md bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-700 capitalize">
                              {user.role?.toLowerCase()}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-500">{user.email}</p>
                        </div>
                        {canManageUsers() && user.role === 'ANALYST' && (
                          <button
                            onClick={() => {
                              // This will be handled by the manage users modal
                              setShowManageUsersModal(true);
                            }}
                            className="text-error-600 hover:text-error-700 text-sm"
                          >
                            {t('common.delete')}
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-6 text-center text-neutral-500">
                  <div className="w-12 h-12 bg-neutral-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <h4 className="text-sm font-medium text-neutral-900 mb-2">{t('workspaces.noAnalystsAssigned')}</h4>
                  <p className="text-sm text-neutral-500 mb-4">
                    {t('workspaces.noAnalystsDescription')}
                  </p>
                  {canManageUsers() && (
                    <button
                      onClick={() => setShowManageUsersModal(true)}
                      className="text-accent-600 hover:text-accent-700 font-medium text-sm"
                    >
                      {t('workspaces.assignAnalystsButton')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Case Modal */}
      {showCreateCaseModal && (
        <CreateCaseModal
          workspaceId={project.id}
          onClose={() => setShowCreateCaseModal(false)}
          onSubmit={handleCreateCase}
        />
      )}

      {/* Manage Users Modal */}
      {showManageUsersModal && (
        <ManageUsersModal
          workspace={project}
          onClose={() => {
            setShowManageUsersModal(false);
            fetchProject(); // Refresh project data to update participant count
          }}
        />
      )}
    </div>
  );
};

export default ProjectDetail;